import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './RegisterPage.module.css';
import { useRegister } from './hooks/useAuth';
import { SendRegistrationEmailRequest } from './types';

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SendRegistrationEmailRequest>({
    name: '',
    organization: '',
    phone: '',
    email: '',
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const { register, loading } = useRegister();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await register(formData);
    if (success) {
      setIsSuccess(true);
    }
  };

  if (isSuccess) {
    return (
      <div className={styles.registerPage}>
        <div className={styles.registerPanel}>
          <div className={styles.successState}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle} data-testid="register-success-title">Request Sent Successfully!</h2>
            <p className={styles.successMessage} data-testid="register-success-message">
              Thank you for your interest in ShaktiSetu. Our team will review your details and get back
              to you shortly at <strong>{formData.email}</strong>.
            </p>
            <button className={styles.buttonPrimary} onClick={() => navigate('/login')} data-testid="register-back-to-login">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.registerPage}>
      <div className={styles.registerPanel}>
        <div className={styles.registerHeader}>
          <h1 className={styles.registerTitle} data-testid="register-title">Join ShaktiSetu</h1>
          <p className={styles.registerSubtitle} data-testid="register-subtitle">
            Register your organization to start hiring verified talent.
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.formLabel}>
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className={styles.formInput}
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., John Doe"
              required
              data-testid="register-name-input"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="organization" className={styles.formLabel}>
              Organization Name
            </label>
            <input
              id="organization"
              name="organization"
              type="text"
              className={styles.formInput}
              value={formData.organization}
              onChange={handleChange}
              placeholder="e.g., Acme Corp"
              required
              data-testid="register-organization-input"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="phone" className={styles.formLabel}>
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              className={styles.formInput}
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g., +91 98765 43210"
              required
              data-testid="register-phone-input"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.formLabel}>
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              className={styles.formInput}
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., john@acme.com"
              required
              data-testid="register-email-input"
            />
          </div>

          <button type="submit" className={styles.buttonPrimary} disabled={loading} data-testid="register-submit-button">
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>

          <div className={styles.loginLink}>
            Already have an account? <span onClick={() => navigate('/login')} data-testid="register-login-link">Log in</span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
